import type { ComponentType } from 'react';
import { Hueco } from '../../componentes/EstadoPagina';
import { Segmentos } from '../../componentes/Segmentos';
import { dibujoDe } from '../../marco/iconosMenu';
import { usePermisos } from '../../permisos/permisos';
import { valorDeApartado } from '../apartados';
import { PortalRrpp } from '../portal-rrpp/PortalRrpp';
import type { PropsPantalla } from '../tipos';
import { ApartadoListas } from './listas/ApartadoListas';
import { ApartadoLiquidaciones } from './liquidaciones/ApartadoLiquidaciones';
import { ApartadoRrpp } from './rrpp/ApartadoRrpp';
import { PERMISOS } from './textos';
import '../../componentes/apartado.css';
import './listas.css';

/**
 * «Listas y RRPP» (10.9 a 10.11, entrada `listas`, G778) con su barra de apartados (RV Segmentos): Listas · RRPP ·
 * Liquidaciones. Los apartados los declara el servidor (`listas_listas`, `listas_rrpp`, `listas_liquidaciones`, con
 * `nested_as: section`), en su orden y solo si la persona tiene su permiso (G120: quien no lo tiene no lo ve); cada uno
 * en `?apartado=`. Sin `?apartado=`, el primero que llega. `?invitado=<id>` abre la ficha del invitado (destino del aviso
 * `listas.pase_no_entregado`, G791).
 *
 * El RRPP (permiso `listas.listas.ver` con «lo suyo») no usa esta pantalla sino su portal (10.12).
 */
const APARTADOS: Readonly<Partial<Record<string, ComponentType<PropsPantalla>>>> = {
    listas_listas: ApartadoListas,
    listas_rrpp: ApartadoRrpp,
    listas_liquidaciones: ApartadoLiquidaciones,
};

const CORTAS: Readonly<Record<string, string>> = { listas_liquidaciones: 'Liquidar' };

export function Listas(props: PropsPantalla) {
    const permisos = usePermisos();
    if (permisos.ambito(PERMISOS.listasVer) === 'own') {
        return <PortalRrpp {...props} />;
    }
    return <ListasDelPanel {...props} />;
}

function ListasDelPanel(props: PropsPantalla) {
    const { busqueda, ruta, navegar, movil } = props;
    const apartados = (props.anidadas ?? [])
        .filter((e) => e.anidadaComo === 'section' && !e.noContratada)
        .map((e) => ({ clave: e.clave, etiqueta: e.etiqueta, corta: CORTAS[e.clave], icono: dibujoDe(e.icono), valor: valorDeApartado(e.clave, e.ruta, 'listas_'), ruta: e.ruta, cifra: e.contador }));
    // `?invitado=` sin apartado: la ficha del invitado está en Listas.
    const pedido = busqueda.get('apartado') ?? (busqueda.has('invitado') ? 'listas' : null);
    const activo = apartados.find((a) => a.valor === pedido) ?? apartados[0];
    if (activo === undefined) {
        return <Hueco seccion="Listas y RRPP" icono="list-checks" />;
    }
    const Apartado = APARTADOS[activo.clave];
    return (
        <div className="rv-ls-pantalla">
            {apartados.length > 1 && (
                <div className="rv-ls-barra">
                    <Segmentos
                        etiqueta="Apartados de listas y RRPP"
                        movil={movil}
                        items={apartados.map((a) => ({ etiqueta: a.etiqueta, corta: a.corta, icono: a.icono, cifra: a.cifra }))}
                        valor={apartados.indexOf(activo)}
                        onCambio={(i) => {
                            const a = apartados[i];
                            if (a !== undefined) {
                                navegar(a.ruta.includes('?') ? a.ruta : `${ruta}?apartado=${encodeURIComponent(a.valor)}`);
                            }
                        }}
                    />
                </div>
            )}
            {Apartado === undefined ? <Hueco seccion={activo.etiqueta} icono={activo.icono} /> : <Apartado key={activo.clave} {...props} />}
        </div>
    );
}
